import { InvoiceInfo } from '@cart-ui/InvoiceInfo/InvoiceInfo'
import { MerchantInfo } from '@cart-ui/MerchantInfo/MerchantInfo'
import { OrderNote } from '@cart-ui/OrderNote/OrderNote'
import { RecipientInfo } from '@cart-ui/RecipientInfo/RecipientInfo'
import { ShippingInfo } from '@cart-ui/ShippingInfo/ShippingInfo'
import { type ChildOrder } from '@hooks-api/types/dataModel/fpOrder'
import { ShippingMethodMap, SHIPPING_METHOD_TYPE } from '@hooks-api/types/dataModel/shipping'
import { Box } from '@pets-ui/Box/Box'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { resolveSfStoreType } from '@utils/resolve-sf-store-type'
import { useTranslation } from 'react-i18next'
import { useContextOrderDetail } from '../OrderDetailProvider/OrderDetailProvider'
import { OrderAmount } from './OrderAmount'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const orderInfoStyles = stylex.create({
  layout: {
    gap: 20,
    display: {
      [MOBILE]: 'block',
      default: 'grid',
    },
    gridTemplateColumns: '1fr 1fr',
  },
  info: {
    padding: 12,
    borderColor: colors.grey300,
    borderStyle: 'solid',
    borderWidth: '1px 0 0 0',
    gap: 4,
    direction: 'ltr',
    borderTopColor: colors.grey300,
    borderTopWidth: 1,
  },
  orderAmountBox: {
    direction: 'rtl',
  },
})

interface OrderInfoProps {
  order: ChildOrder
  sectionStyle?: stylex.StyleXStyles
}

export function OrderInfo({ order, sectionStyle }: OrderInfoProps) {
  const { showMerchantInfo, showRecipientInfo, countryCode, transaction } = useContextOrderDetail()
  const infoStyle = [orderInfoStyles.info, sectionStyle]
  const {
    delivery_time_slot,
    delivery_date,
    customer_note,
    merchant_profile: merchantProfile,
    order_invoice: orderInvoice,
    shipping_address_phone: shippingPhone,
    shipping_address_first_name: shippingName,
    shipping_method: shippingMethod,
  } = order
  const { t } = useTranslation()
  const isCommunityPickup = shippingMethod === SHIPPING_METHOD_TYPE.PICKUP_FAMILY_MART_COMMUNITY
  const isCvsInfoPickup = shippingMethod === SHIPPING_METHOD_TYPE.PICKUP_711 || isCommunityPickup
  const shippingAddress = isCvsInfoPickup ? transaction?.cvs_info.store_address : transaction?.shipping_address
  const scvStoreName = isCvsInfoPickup ? transaction?.cvs_info.store_name : null
  const cvsStoreLabel = isCommunityPickup ? t('orderDetail.pickupCommunity', { defaultValue: '取件社區' }) : undefined
  const normalizedShippingMethod = resolveSfStoreType(shippingMethod) ?? shippingMethod
  const shippingMethodName = ShippingMethodMap[normalizedShippingMethod]
  const shippingMethodText = t(`shipping.${shippingMethodName}`, { defaultValue: shippingMethodName })

  return (
    <VStack p={3} gap={3}>
      <VStack gap={3}>
        <Box rootStyle={orderInfoStyles.layout}>
          {showRecipientInfo
          && (
            <RecipientInfo
              recipientName={merchantProfile?.shipping_address_first_name}
              email={merchantProfile?.shipping_address_email}
              phone={merchantProfile?.shipping_address_phone}
              rootStyle={infoStyle}
            />
          )}
          <>
            <ShippingInfo
              shippingAddress={shippingAddress}
              shippingPhone={shippingPhone}
              shippingName={shippingName}
              rootStyle={infoStyle}
              shippingMethodTitle={shippingMethodText}
              countryCode={countryCode}
              cvsStoreName={scvStoreName}
              cvsStoreLabel={cvsStoreLabel}
              postalCode={transaction?.shipping_address_postcode}
            />
            <OrderNote deliveryDate={delivery_date} deliveryTimeSlot={delivery_time_slot} customerNote={customer_note} rootStyle={infoStyle} />
          </>
          {showMerchantInfo
          && (
            <MerchantInfo
              merchantName={merchantProfile?.shipping_address_first_name}
              email={merchantProfile?.shipping_address_email}
              phone={merchantProfile?.shipping_address_phone}
              rootStyle={infoStyle}
            />
          )}
          <InvoiceInfo
            invoiceNumber={orderInvoice?.invoice_number}
            receiptType={orderInvoice?.receipt_type}
            businessUbnTitle={orderInvoice?.business_ubn_title}
            businessUbn={orderInvoice?.business_ubn}
            phoneCarrierNumber={orderInvoice?.phone_carrier_number}
            charityOrganization={orderInvoice?.charity_organization}
            isRefundStatement={orderInvoice?.refund_statement}
            rootStyle={infoStyle}
            isInOrderDetailPage
            countryCode={countryCode}
            projectCode={order.project_code}
          />
        </Box>
        <Box rootStyle={[orderInfoStyles.layout, orderInfoStyles.orderAmountBox]}>
          <OrderAmount order={order} rootStyle={infoStyle} />
        </Box>
      </VStack>
    </VStack>
  )
}
